import React, { useState, useEffect } from 'react';
import { MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { ref, update, get } from 'firebase/database';
import { database } from '../../firebase/config';
import { GENRE_CATEGORIES, MAJOR_CITIES, DUTCH_REGIONS } from '../../constants/genreCategories';

export default function BandProfile({ user, bandId, bandData, setError, showMessage }) {
  const [expanded, setExpanded] = useState(false);
  const [bandCity, setBandCity] = useState('');
  const [bandRegion, setBandRegion] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubgenres, setSelectedSubgenres] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || !bandId) return;
    const loadProfile = async () => {
      try {
        const bandRef = ref(database, `bands/${bandId}`);
        const snapshot = await get(bandRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data.location) setBandCity(data.location);
          if (data.region) setBandRegion(data.region);
          if (data.genreCategories) setSelectedCategories(data.genreCategories);
          if (data.genres) setSelectedSubgenres(data.genres);
        }
      } catch (error) {
        console.error('Error loading band profile:', error);
      }
    };
    loadProfile();
  }, [user, bandId]);

  const handleCityChange = (city) => {
    setBandCity(city);
    const cityRegionMap = {
      'Amsterdam': 'Noord Holland', 'Rotterdam': 'Zuid Holland', 'Den Haag': 'Zuid Holland',
      'Utrecht': 'Utrecht', 'Eindhoven': 'Noord Brabant', 'Groningen': 'Groningen',
      'Tilburg': 'Noord Brabant', 'Almere': 'Flevoland', 'Breda': 'Noord Brabant',
      'Nijmegen': 'Gelderland', 'Enschede': 'Overijssel', 'Haarlem': 'Noord Holland',
      'Arnhem': 'Gelderland', 'Zaanstad': 'Noord Holland', 'Amersfoort': 'Utrecht',
      'Leiden': 'Zuid Holland', 'Dordrecht': 'Zuid Holland', 'Delft': 'Zuid Holland',
      'Maastricht': 'Limburg'
    };
    if (cityRegionMap[city]) setBandRegion(cityRegionMap[city]);
  };

  const toggleCategory = (categoryKey) => {
    if (selectedCategories.includes(categoryKey)) {
      setSelectedCategories(selectedCategories.filter(c => c !== categoryKey));
      const categorySubgenres = GENRE_CATEGORIES[categoryKey].subgenres;
      setSelectedSubgenres(selectedSubgenres.filter(g => !categorySubgenres.includes(g)));
    } else {
      setSelectedCategories([...selectedCategories, categoryKey]);
    }
  };

  const toggleSubgenre = (subgenre) => {
    if (selectedSubgenres.includes(subgenre)) {
      setSelectedSubgenres(selectedSubgenres.filter(g => g !== subgenre));
    } else {
      setSelectedSubgenres([...selectedSubgenres, subgenre]);
    }
  };

  const toggleCategoryExpansion = (categoryKey) => {
    if (expandedCategories.includes(categoryKey)) {
      setExpandedCategories(expandedCategories.filter(c => c !== categoryKey));
    } else {
      setExpandedCategories([...expandedCategories, categoryKey]);
    }
  };

  const saveProfile = async () => {
    if (!user || !bandId) return;
    try {
      setSaving(true);
      setError('');
      await update(ref(database, `bands/${bandId}`), {
        location: bandCity || null,
        region: bandRegion || null,
        genreCategories: selectedCategories.length > 0 ? selectedCategories : null,
        genres: selectedSubgenres.length > 0 ? selectedSubgenres : null,
        profileUpdatedAt: new Date().toISOString()
      });
      showMessage('Band profile updated successfully!');
    } catch (error) {
      setError('Failed to update band profile: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-3d rounded-card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left"
      >
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <MapPin className="w-5 h-5 text-white" />
          Band Profile
        </h2>
        {expanded ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
      </button>

      {expanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6">
          <p className="text-sm text-text-subtle mb-6">
            Help venues find you! Add your location and music genres to get personalized venue recommendations.
          </p>

          <div className="space-y-6">
            {/* Location */}
            <div>
              <label className="text-sm font-semibold mb-2 block">📍 Location</label>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-text-subtle mb-1 block">City</label>
                  <select
                    value={bandCity}
                    onChange={(e) => handleCityChange(e.target.value)}
                    className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white focus:outline-none focus:border-brand rounded-pill"
                  >
                    <option value="">Select your city...</option>
                    {MAJOR_CITIES.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                    <option value="other">Other (specify in region)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-text-subtle mb-1 block">Region</label>
                  <select
                    value={bandRegion}
                    onChange={(e) => setBandRegion(e.target.value)}
                    className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white focus:outline-none focus:border-brand rounded-pill"
                  >
                    <option value="">Select region...</option>
                    {DUTCH_REGIONS.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Genre Categories */}
            <div>
              <label className="text-sm font-semibold mb-2 block">🎵 Music Genres</label>
              <p className="text-xs text-text-subtle mb-3">
                Select your main style(s), then pick specific sub-genres for better venue matching.
              </p>

              <div className="space-y-2">
                {Object.entries(GENRE_CATEGORIES).map(([categoryKey, category]) => {
                  const isCategorySelected = selectedCategories.includes(categoryKey);
                  const isExpanded = expandedCategories.includes(categoryKey);
                  const selectedCount = category.subgenres.filter(g => selectedSubgenres.includes(g)).length;

                  return (
                    <div key={categoryKey} className="border border-surface-border-light rounded-card-btn overflow-hidden">
                      <div className="flex items-center gap-2 p-3">
                        <input
                          type="checkbox"
                          checked={isCategorySelected}
                          onChange={() => toggleCategory(categoryKey)}
                          className="w-4 h-4 rounded border-slate-500 text-brand focus:ring-brand"
                        />
                        <button
                          onClick={() => toggleCategoryExpansion(categoryKey)}
                          className="flex-1 flex items-center justify-between text-left hover:bg-white/5 px-2 py-1 rounded transition"
                        >
                          <span className="font-semibold">
                            {category.icon} {category.name}
                            {selectedCount > 0 && (
                              <span className="ml-2 text-xs text-brand">({selectedCount} selected)</span>
                            )}
                          </span>
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-text-subtle" /> : <ChevronDown className="w-4 h-4 text-text-subtle" />}
                        </button>
                      </div>

                      {isExpanded && isCategorySelected && (
                        <div className="border-t border-surface-border-light p-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {category.subgenres.map(subgenre => (
                              <label
                                key={subgenre}
                                className="flex items-center gap-2 text-sm hover:bg-white/5 px-2 py-1 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedSubgenres.includes(subgenre)}
                                  onChange={() => toggleSubgenre(subgenre)}
                                  className="w-3 h-3 rounded border-slate-500 text-brand focus:ring-brand"
                                />
                                <span className="text-text-muted">{subgenre}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedSubgenres.length > 0 && (
                <div className="mt-4 p-3 border border-surface-border-light rounded-card-btn">
                  <p className="text-xs text-text-subtle mb-2">
                    Selected genres ({selectedSubgenres.length}):
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedSubgenres.map(genre => (
                      <span
                        key={genre}
                        className="inline-flex items-center gap-1 bg-brand px-2 py-1 rounded-full text-xs"
                      >
                        {genre}
                        <button onClick={() => toggleSubgenre(genre)} className="hover:text-red-300">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={saveProfile}
              disabled={saving}
              className="w-full bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition h-btn font-semibold rounded-pill"
            >
              {saving ? 'Saving...' : 'Save Band Profile'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
